import { selectHistoryDataLoading } from 'features/Integrations/state/selectors/integrationSelectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { buildRow, formatMetric, formatTimestamp } from './DatasheetBuilderUtils';

const sortBuoyRecords = (device) =>
  [...(Array.isArray(device?.data) ? device.data : [])].sort(
    (a, b) => (b?.ts ?? 0) - (a?.ts ?? 0),
  );

const buildBuoyRows = (device, current) => [
  buildRow('Buoy Name', device?.alias || device?.name || 'N/A'),
  buildRow('Buoy ID', device?.ext_id || 'N/A'),
  buildRow('Node ID', device?.node_id || 'N/A'),
  buildRow('GPS Location', device?.location ? `${device.location.lat} N ${device.location.lng} E` : 'N/A'),
  buildRow('Wind Speed', formatMetric(current?.wind_speed, 'MPH')),
  buildRow('Wind Gusts', formatMetric(current?.wind_gusts, 'MPH')),
  buildRow('Wave Height', formatMetric(current?.wave_height, 'ft')),
  buildRow('Wave Period', formatMetric(current?.wave_period, 'sec')),
  buildRow('Air Temp', formatMetric(current?.air_temperature, '°F')),
  buildRow('Dew Point', formatMetric(current?.dew_point, '°F')),
  buildRow('Water Temp', formatMetric(current?.water_temperature, '°F')),
  buildRow('Barometric Pressure', formatMetric(current?.baro_pressure, 'inHg')),
  buildRow('Visibility', formatMetric(current?.visibility, 'mi')),
  buildRow('Wind Direction', current?.wind_direction || 'N/A'),
  buildRow('Beaufort', current?.beaufort ?? 'N/A'),
  buildRow('Beaufort Description', current?.beaufort_desc || 'N/A'),
];

export const useBuoyDatasheetBuilder = ({
  device,
  onClose,
  onSettingsOpen,
  footer,
  loadMore,
  hasMore = false,
  isFetching = false,
  error = null,
}) => {
  const historyDataLoading = useSelector(selectHistoryDataLoading);
  const records = useMemo(() => sortBuoyRecords(device), [device]);
  const errorMessage = error?.message ?? null;

  return useMemo(
    () => ({
      title: 'Buoy Data',
      records,
      loading: historyDataLoading || isFetching,
      loadingMessage: 'Loading buoy data...',
      emptyMessage: 'No data available for this buoy.',
      // The fetch failed: say so with the API's own words instead of "no data".
      errorMessage,
      onClose,
      onSettingsOpen,
      settingsDisabled: !onSettingsOpen,
      footer,
      onLoadMore: loadMore,
      hasMore,
      loadingMore: isFetching,
      getRecordId: (record) => record?.observation_id,
      subtitle: ({ ts } = {}) => `Time Stamp: ${formatTimestamp(ts)}`,
      sections: (current) => [
        {
          key: 'buoy-conditions',
          title: 'Buoy Conditions',
          rows: buildBuoyRows(device, current),
        },
      ],
    }),
    [
      device,
      records,
      historyDataLoading,
      isFetching,
      errorMessage,
      onClose,
      onSettingsOpen,
      footer,
      loadMore,
      hasMore,
    ],
  );
};
